// El estado de cada fila de la tabla de un lanzamiento, en palabras —
// ADR-2026-10-05-la-tabla-de-la-tienda-dice-que-falta.
//
// Antes la columna de estado solo pintaba algo DESPUÉS de publicar (✓, ✕, girando).
// Mientras se armaba, la fila no decía si estaba lista o qué le faltaba: había que
// apretar "Publicar" para enterarse. Ahora cada fila lleva una pastilla —"Listo para
// publicar", "Falta el precio"— que sale de la MISMA validación que frena la
// publicación (`problemasParaPublicar`), así que la tabla y el botón no pueden
// contradecirse.
import { faltaParaMl } from '../../lib/mercadoLibrePublicarLogica';
import { problemasParaPublicar, type ProductoDeLanzamiento } from './lanzamientosApi';
import type { CanalLanzamiento } from './canales';

export type TonoDeEstado = 'listo' | 'falta' | 'publicado' | 'publicando' | 'error';

export interface EstadoDeFila {
  tono: TonoDeEstado;
  /** Lo que dice la pastilla: corto, entra en una celda. */
  texto: string;
  /** La explicación completa, para el `title` y el lector de pantalla. */
  detalle: string | null;
  /**
   * La columna de la tabla donde está el hueco, si es una de las que se ven: esa
   * celda dice "Falta" en ámbar en vez del guion gris de un dato opcional.
   */
  columna?: 'precio' | 'sku' | 'stock';
}

const mayuscula = (t: string) => (t ? t[0]!.toUpperCase() + t.slice(1) : t);

/** Qué columna de la tabla señala la falta, si alguna. */
export function columnaDeLaFalta(texto: string): EstadoDeFila['columna'] {
  if (texto === 'Falta el precio') return 'precio';
  if (texto === 'Falta el SKU') return 'sku';
  return undefined;
}

/**
 * El motivo de `problemasParaPublicar` es una frase para un aviso ("faltan el peso y
 * las medidas del paquete (Mercado Libre los exige para crear el producto)"). En la
 * pastilla va lo esencial; la frase entera queda en `detalle`.
 */
export function textoCorto(motivo: string): string {
  const m = motivo.toLowerCase();
  if (m === 'no tiene fotos') return 'Sin fotos';
  if (m.startsWith('todavía no está atado')) return 'Sin producto del catálogo';
  if (m === 'falta el precio') return 'Falta el precio';
  if (m === 'falta el nombre') return 'Falta el nombre';
  if (m.includes('variante sin precio')) return 'Variante sin precio';
  if (m.includes('peso') || m.includes('medidas')) return 'Faltan peso y medidas';
  if (m.includes('sku o un código de barras')) return 'Falta el SKU';
  if (m.includes('supera')) return 'Texto muy largo';
  if (m.includes('variante')) return 'Revisar variantes';
  if (m.includes('categoría')) return 'Revisar categoría';
  return 'Revisar la ficha';
}

/**
 * Qué decir de un producto. Lo que ya pasó por la tienda (publicando, publicado, con
 * error) gana sobre lo que le falta: un producto publicado no está "listo para
 * publicar", ya está.
 */
export function estadoDeFila(
  p: ProductoDeLanzamiento,
  destinos: readonly CanalLanzamiento[],
  verbo: 'publicar' | 'descargar' = 'publicar',
): EstadoDeFila {
  if (p.estado === 'publicando') return { tono: 'publicando', texto: 'Publicando', detalle: null };
  if (p.estado === 'error') {
    return { tono: 'error', texto: 'No se pudo publicar', detalle: p.error ? mayuscula(p.error) : null };
  }
  if (p.estado === 'publicado') return { tono: 'publicado', texto: 'Publicado', detalle: null };

  const problema = problemasParaPublicar([p], destinos)[0];
  if (problema) {
    const texto = textoCorto(problema.motivo);
    return { tono: 'falta', texto, detalle: mayuscula(problema.motivo), columna: columnaDeLaFalta(texto) };
  }
  // Lo propio de Mercado Libre (categoría, sus datos, color, guía de talles) no está en la ficha común:
  // sin esto la pastilla decía "Listo para publicar" con la sección de Mercado Libre pidiendo datos
  // (prueba en vivo del 16/10). Es el MISMO chequeo que frena el botón (`frenoDeMl`).
  if (destinos.includes('mercadolibre')) {
    const falta = faltaParaMl(p, p.mercadolibre);
    if (falta) {
      const stock = falta === 'falta el stock';
      return { tono: 'falta', texto: stock ? 'Falta el stock' : 'Completar Mercado Libre', detalle: mayuscula(falta), columna: stock ? 'stock' : undefined };
    }
  }
  return {
    tono: 'listo',
    texto: verbo === 'descargar' ? 'Listo para descargar' : 'Listo para publicar',
    detalle: null,
  };
}

/** Cuántos de cada tono hay: alimenta el panel de arriba de la tabla. */
export function contarEstados(
  productos: readonly ProductoDeLanzamiento[],
  destinos: readonly CanalLanzamiento[],
): Record<TonoDeEstado, number> {
  const cuenta: Record<TonoDeEstado, number> = { listo: 0, falta: 0, publicado: 0, publicando: 0, error: 0 };
  for (const p of productos) cuenta[estadoDeFila(p, destinos).tono]++;
  return cuenta;
}
